import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import Loader from "../components/Loader";
import { LOCALES } from "common/constants";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

const AuthorizedForReminder = () => {
  const [loading, setLoading] = useState(false); // Set initial loading to false
  const [error, setError] = useState(null);
  const [invoiceDetails, setInvoiceDetails] = useState(null); // State to store API response
  const [isReminderRequested, setIsReminderRequested] = useState(false); // Track if reminder is requested
  const navigate = useNavigate();
  const location = useLocation();
  const language = localStorage.getItem("i18nextLng");
  const { t } = useTranslation();

  // Function to extract query params from URL
  const getQueryParams = (query) => {
    return new URLSearchParams(query);
  };

  useEffect(() => {
    fetchInvoiceDetails(); // Call fetchInvoiceDetails when the page loads
  }, [location.search]);

  const fetchInvoiceDetails = async () => {
    setLoading(true);
    try {
      const queryParams = getQueryParams(location.search);
      const invoiceNumber = queryParams.get("invoiceNumber");
      const token = queryParams.get("token");

      const response = await axios.post("/reminderInvoice/get-invoice-details", {
        InvoiceNumber: invoiceNumber,
        Token: token,
      });

      if (response.status !== 200) {
        throw new Error(t("Failed to fetch invoice details."));
      }

      setInvoiceDetails(response.data); // Save response in state
      setLoading(false);
    } catch (err) {
      setError(err?.response?.data?.message || "An error occurred while fetching invoice details.");
      setLoading(false);
    }
  };

  const updateAuthorization = async () => {
    setLoading(true);
    try {
      const queryParams = getQueryParams(location.search);
      const invoiceNumber = queryParams.get("invoiceNumber");
      const token = queryParams.get("token");

      const response = await axios.put("/reminderinvoice/update-authorization", {
        invoiceNumber,
        token,
      });

      if (response.status !== 200) {
        throw new Error(t("Failed to authorize reminder."));
      }
     toast.success("Reminder request sent successfully.");
      setIsReminderRequested(true);
      setLoading(false);
      navigate(
        language === LOCALES.EN_US
          ? `/dashboard/activities?lang=${LOCALES.EN_US}`
          : `/instrumentbrada/aktiviteter?lang=${LOCALES.SV_SE}`
      );
    } catch (err) {
      setError(err?.response?.data?.error || "An error occurred while authorizing.");
      setLoading(false);
    }
  };

  const closeTab = () => {
    if (window.opener) {
      window.close();
    } else {
      navigate(
        language === LOCALES.EN_US
          ? `/dashboard/activities?lang=${LOCALES.EN_US}`
          : `/instrumentbrada/aktiviteter?lang=${LOCALES.SV_SE}`
      );
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-100">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100" style={{ backgroundColor: "#EAE2F8" }}>
        <div
          className="card shadow-lg text-center p-4 p-md-5"
          style={{
            maxWidth: "400px",
            borderRadius: "20px",
            boxShadow: "0px 8px 15px rgba(0, 0, 0, 0.2)",
          }}
        >
          <h2 className="text-danger mb-3" style={{ fontSize: "1.8rem", fontWeight: "bold" }}>
            {t("Authorization Failed")}
          </h2>
          <p className="text-muted" style={{ fontSize: "1rem", lineHeight: "1.5",fontWeight: 'bold' }}>{error}</p>
          <hr />
          <button
            className="btn btn-danger"
            style={{
              fontSize: "1rem",
              fontWeight: "bold",
              padding: "10px 20px",
              borderRadius: "5px",
              boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
            }}
            onClick={closeTab}
          >
            {t("Close")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex justify-content-center align-items-center vh-100" style={{ backgroundColor: "#EAE2F8" }}>
      <div
        className="card shadow-lg text-center p-4 p-md-5"
        style={{
          maxWidth: "600px",
          borderRadius: "20px",
          boxShadow: "0px 8px 15px rgba(0, 0, 0, 0.2)",
        }}
      >
        <h2
          className="text-primary mb-3"
          style={{
            color: "#4A90E2",
            fontSize: "2.2rem",
            fontWeight: "bold",
            textShadow: "1px 1px 2px rgba(0, 0, 0, 0.2)",
          }}
        >
          {t("Request Reminder")}
        </h2>
        {invoiceDetails && (
          <div>
            <p className="text-muted" style={{ fontSize: "1.1rem", fontWeight: "500" }}>
              <b>{t("Invoice Number:")} #{invoiceDetails.invoiceNumber}</b>
            </p>
            <p className="text-muted" style={{ fontSize: "1.1rem", fontWeight: "500" }}>
              <b>{t("Invoice Amount inc. VAT:")} {invoiceDetails.amountIncVat}</b>
            </p>
            <p className="text-muted" style={{ fontSize: "1.1rem", fontWeight: "500" }}>
              <b>{t("Due Date:")} {new Date(invoiceDetails.dueDate).toLocaleDateString()}</b>
            </p>
            <p className="text-muted" style={{ fontSize: "1rem", lineHeight: "1.5" }}>
              <b>{t("Click on request button to ask our team to send a reminder to your customer. This will be charged.")}</b>
            </p>
          </div>
        )}
        <hr />
        <div className="d-flex justify-content-between">
          <button
            className="btn btn-link text-decoration-none ms-1"
            style={{
              color: "#886cc1",
              fontWeight: "bold",
              fontSize: "1rem",
              textDecoration: "underline",
            }}
            onClick={() =>
              navigate(
                language === LOCALES.EN_US
                  ? `/dashboard/activities?lang=${LOCALES.EN_US}`
                  : `/instrumentbrada/aktiviteter?lang=${LOCALES.SV_SE}`
              )
            }
          >
           {t("Home page")}
          </button>
          <button
            className="btn btn-primary"
            style={{
              backgroundColor: isReminderRequested ? "#886cc1" : "#886cc1",
              color: "#FFFFFF",
              fontWeight: "bold",
              fontSize: "1rem",
              border: "none",
              padding: "12px 25px",
              borderRadius: "8px",
              boxShadow: "0px 6px 10px rgba(0, 0, 0, 0.15)",
              transition: "background-color 0.3s ease",
            }}
            onClick={updateAuthorization}
            disabled={isReminderRequested}
          >
            {isReminderRequested ? t("Reminder Requested") : t("Request Reminder")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthorizedForReminder;
